import React, { useState, useRef, useEffect } from "react";
import FileUpload from "../components/FileUpload";
import RecommendedJobs from "../components/RecommendedJobs";
import SkillGaps from "../components/SkillGaps";
import { motion } from "framer-motion";
import "../styles/Home.css";
import { authHelper } from "../Helpers/authHelper";
import { RECOMMENDATION_STATUS } from "../Enums/enums";

export default function Home() {
  const [skills, setSkills] = useState([]);
  const pollingRef = useRef(null); // <-- used to track polling interval
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);

  async function handlePolling(fileName, recommendationId) {
    try {
      const authHelperInstance = new authHelper();
      const user = await authHelperInstance.getCurrentUser();
      if (!user) {
        console.error("User not authenticated");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid, recommendationId }),
      });

      if (!response.ok) throw new Error("Failed to poll for recommendations");

      const data = await response.json();
      setData(data); // Store the data for potential future use
      if (data.status === RECOMMENDATION_STATUS.RECOMMENDATION_COMPLETED) {
        stopPolling(); // stop polling if done
        const recommendations = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/get-recommendations`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: user.uid, recommendationId }),
          }
        );

        if (!recommendations.ok)
          throw new Error("Failed to fetch recommendations");

        const data = await recommendations.json();
        setJobs(data); // Store the recommendations in state
        if (!data || data.length === 0) {
          console.error("No recommendations found");
          return;
        }

        // Successfully fetched recommendations
        console.log("📥 Recommendations fetched:", data);
        stopPolling(); // stop polling if done
        // You can update state or UI here with data.recommendations
      } else {
        console.log("⏳ Recommendations not ready, polling again...");
      }
    } catch (error) {
      console.error("Polling error:", error);
      stopPolling();
    } 
  }

  function startPolling(fileName, recommendationId) {
    if (pollingRef.current) {
      console.warn("Polling already active, skipping new start.");
      return; // prevent multiple intervals
    }

    // First call immediately
    handlePolling(fileName, recommendationId);

    // Then keep polling every 5 seconds
    pollingRef.current = setInterval(() => {
      handlePolling(fileName, recommendationId);
    }, 5000);

    console.log("🚀 Polling started");
  }

  function stopPolling() {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      console.log("🛑 Polling stopped");
    }
  }

  useEffect(() => {
    return () => {
      stopPolling(); // cleanup on unmount
    };
  }, []);

  async function generateRecommendations(fileName) {
    try {
      const authHelperInstance = new authHelper();
      const user = await authHelperInstance.getCurrentUser();
      if (!user) {
        console.error("User not authenticated");
        return;
      }
      setData(null); // Reset data before generating new recommendations
      setJobs([]); // Reset jobs before generating new recommendations

      const recommendationId = crypto.randomUUID();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/generate-recommendation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName,
            userId: user.uid,
            recommendationId,
          }),
        }
      );

      const data = await response.json();
      console.log("📤 Recommendation request sent:", data);

      // Start polling after the request is successfully sent
      startPolling(fileName, recommendationId);
    } catch (error) {
      console.error("Error generating recommendations:", error);
    }
  }

  return (
    <motion.div
      className="home-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.h1
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="home-title"
      >
        💼 Dynamic Job Portal
      </motion.h1>

      <motion.div
        className="section"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <FileUpload
          onSkillsExtracted={setSkills}
          generateRecommendations={generateRecommendations}
          data={data}
          jobs={jobs}
        />
      </motion.div>

      <motion.div
        className="section"
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <RecommendedJobs uploadedSkills={skills} data={data} jobs={jobs} />
      </motion.div>

      {/* <motion.div
        className="section"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.5 }}
      >
        <SkillGaps uploadedSkills={skills} />
      </motion.div> */}
    </motion.div>
  );
}
